'use strict'
import * as React from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import { addDaysToDateString, normalizeErrorsForShow } from '../extra/helpers'
import { validateNewPeriod } from '../extra/validation'
import { SpinnerButton } from '../../../common/CustomButton'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'
import { DepartmentDatePicker } from './DepartmentDatePicker'
import { ChrofyShowErrors } from '../../../common/Form'

registerLocale('ja', ja)

/**
 * 新規期間作成モーダル用ボディ（設計書 4.4・資格情報設定と同様の二列レイアウト）
 *
 * @param {Object} props
 * @param {string} props.minStartDate - YYYY-MM-DD または YYYY/MM/DD（最新期間開始日以降）
 * @param {Object[]} props.existingPeriods - 既存期間一覧（startDate を持つ）
 * @param {() => void} [props.onClose] - キャンセル時
 * @param {(startDate: string, copyLatest: boolean) => void | Promise<void>} props.onCreate - (startDate YYYY/MM/DD,
 *   copyLatest)
 * @param {(errors: { start_date?: string }) => void} [props.onError] - エラー時
 * @returns {JSX.Element}
 */
export function DepartmentNewPeriodModal({ minStartDate = '', existingPeriods = [], onClose, onCreate, onError }) {
  const [startDate, setStartDate] = React.useState(null)
  const [copyLatest, setCopyLatest] = React.useState(false)
  const [errors, setErrors] = React.useState({})

  // minDate: 最新期間の開始日 +2 日以降のみ選択可能（前の期間の終了日を新期間開始日の前日とするため）
  let minDate = null
  if (minStartDate) {
    const minSelectableStr = addDaysToDateString(minStartDate, 2)
    if (minSelectableStr) {
      minDate = new Date(minSelectableStr.replace(/-/g, '/'))
    }
  }

  const handleCreate = async () => {
    const dateStr = startDate
      ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
      : ''
    const minForValidation = addDaysToDateString(minStartDate, 1)
    const errs = validateNewPeriod(dateStr, minForValidation || minStartDate?.replace(/-/g, '/'), existingPeriods)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      onError?.(errs)
      return
    }
    setErrors({})
    const apiDate = dateStr.replace(/-/g, '/')
    await Promise.resolve(onCreate(apiDate, copyLatest))
  }

  return (
    <React.Fragment>
      <div className="text-base">
        <table className="form-table table-fixed w-full">
          <tbody>
            {/* 開始年月日 */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="periodStartDate" className="text-h7 font-bold">
                  開始年月日 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <DepartmentDatePicker
                  id="periodStartDate"
                  selected={startDate}
                  onChange={(d) => setStartDate(d)}
                  minDate={minDate}
                  className={`text-input rounded-lg text-center h-[40px] pr-[20px] w-[200px] text-[14px] font-normal ${
                    errors.start_date ? '!border-red-500' : '!border-gray-300'
                  }`}
                  placeholderText="YYYY/MM/DD"
                />
                <ChrofyShowErrors field="start_date" errors={normalizeErrorsForShow(errors)} />
              </td>
            </tr>
            {/* 最新期間データ複製（1列・左寄せ） */}
            <tr>
              <td colSpan={2} className="form-table__col-data pt-2">
                <div className="flex flex-col gap-1 text-left">
                  <label
                    htmlFor="copyLatest"
                    className="inline-flex items-center gap-2 cursor-pointer text-h7 blue1--text"
                  >
                    <Checkbox
                      id="copyLatest"
                      size="small"
                      checkedIcon={<CheckBoxRoundedIcon />}
                      checked={copyLatest}
                      onChange={(e) => setCopyLatest(e.target.checked)}
                    />
                    最新期間の組織情報を複製する
                  </label>
                  <p className="text-caption-2 grey--text">
                    チェックをONにすると、現在の最新期間の組織一覧と組織階層が新しい期間に複製されます。
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="modal-content-footer mt-3">
        <div className="footer_button_area gap-x-16">
          <button
            type="button"
            className="footer-btn button_back"
            data-testid="period_modal_cancel"
            onClick={() => onClose?.()}
          >
            キャンセル
          </button>
          <SpinnerButton
            name="作成"
            className="footer-btn button_base"
            data-testid="period_modal_create"
            onClick={handleCreate}
          />
        </div>
      </div>
    </React.Fragment>
  )
}
